import React, { useState } from 'react';
import { usePreferences } from '../contexts/PreferencesContext';
import { calculateFPU } from '../utils/fpuCalculations';
import { formatCarbs } from '../utils/carbCalculations';
import './RecipeModal.css';

interface RecipeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const RecipeModal: React.FC<RecipeModalProps> = ({
  isOpen,
  onClose
}) => {
  const { 
    preferences,
    getCurrentRecipeIngredients,
    removeIngredientFromRecipe,
    clearCurrentRecipe,
    saveRecipe,
    updateRecipe,
    getCurrentLoadedRecipeId,
    setCurrentLoadedRecipeId,
    getRecipes
  } = usePreferences();
  
  const [recipeName, setRecipeName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [customPortions, setCustomPortions] = useState<Array<{ name: string; grams: number }>>([]);
  const [portionName, setPortionName] = useState('');
  const [portionGrams, setPortionGrams] = useState<string>('');
  
  const isGerman = preferences.language === 'de';
  const ingredients = getCurrentRecipeIngredients();
  const currentLoadedRecipeId = getCurrentLoadedRecipeId();
  const currentLoadedRecipe = currentLoadedRecipeId ? getRecipes().find(r => r.id === currentLoadedRecipeId) : null;
  
  // Calculate totals
  const totals = ingredients.reduce((acc, ingredient) => ({
    grams: acc.grams + ingredient.grams,
    carbs: acc.carbs + ingredient.carbs,
    protein: acc.protein + ingredient.protein,
    fat: acc.fat + ingredient.fat
  }), { grams: 0, carbs: 0, protein: 0, fat: 0 });
  
  // Calculate total FPU/FPE
  const totalFPU = calculateFPU(totals.fat, totals.protein);
  
  const handleRemoveIngredient = (ingredientId: string) => {
    removeIngredientFromRecipe(ingredientId);
  };
  
  const handleClearRecipe = () => {
    const confirmMessage = isGerman 
      ? 'Möchtest Du alle Zutaten aus dem Rezept entfernen?'
      : 'Are you sure you want to remove all ingredients from the recipe?';
    
    if (window.confirm(confirmMessage)) {
      clearCurrentRecipe();
      setCustomPortions([]);
      setCurrentLoadedRecipeId(null); // Clear loaded recipe reference
      onClose();
    }
  };
  
  const handleAddPortion = () => {
    if (!portionName.trim() || !portionGrams.trim()) return;
    
    const grams = parseFloat(portionGrams);
    if (isNaN(grams) || grams <= 0) return;
    
    setCustomPortions(prev => [...prev, { name: portionName.trim(), grams }]);
    setPortionName('');
    setPortionGrams('');
  };
  
  const handleRemovePortion = (index: number) => {
    setCustomPortions(prev => prev.filter((_, i) => i !== index));
  };
  
  const calculatePortionNutrients = (portionGrams: number) => {
    if (totals.grams === 0) return { carbs: 0, protein: 0, fat: 0 };
    
    const ratio = portionGrams / totals.grams;
    return {
      carbs: totals.carbs * ratio,
      protein: totals.protein * ratio,
      fat: totals.fat * ratio
    };
  };
  
  const handleSaveNewRecipe = () => {
    if (ingredients.length === 0) return;
    
    setShowSaveDialog(true);
  };

  const handleUpdateRecipe = () => {
    if (ingredients.length === 0 || !currentLoadedRecipe) return;
    
    setShowUpdateDialog(true);
  };
  
  const handleSaveConfirm = () => {
    if (!recipeName.trim()) {
      alert(isGerman ? 'Bitte gib einen Rezeptnamen ein.' : 'Please enter a recipe name.');
      return;
    }
    
    // Create default portions plus custom portions
    const defaultPortions = [];
    
    // Always add 100g portion unless the whole recipe weighs exactly 100g (within 1g tolerance)
    if (Math.abs(totals.grams - 100) > 1.0) {
      defaultPortions.push({ name: '100g', grams: 100 });
    }
    
    // Always add whole recipe portion
    defaultPortions.push({ 
      name: isGerman ? 'Gesamtes Rezept' : 'Whole recipe', 
      grams: totals.grams 
    });
    
    const allPortions = [...defaultPortions, ...customPortions];
    
    saveRecipe({
      name: recipeName.trim(),
      ingredients: ingredients,
      portions: allPortions
    });
    
    setRecipeName('');
    setCustomPortions([]);
    setShowSaveDialog(false);
    onClose();
  };

  const handleUpdateConfirm = () => {
    if (!currentLoadedRecipe) return;
    
    // Create default portions plus custom portions  
    const defaultPortions = [];
    
    // Always add 100g portion unless the whole recipe weighs exactly 100g (within 1g tolerance)
    if (Math.abs(totals.grams - 100) > 1.0) {
      defaultPortions.push({ name: '100g', grams: 100 });
    }
    
    // Always add whole recipe portion
    defaultPortions.push({ 
      name: isGerman ? 'Gesamtes Rezept' : 'Whole recipe', 
      grams: totals.grams 
    });
    
    const allPortions = [...defaultPortions, ...customPortions];
    
    updateRecipe(currentLoadedRecipe.id, {
      name: currentLoadedRecipe.name, // Keep the same name
      ingredients: ingredients,
      portions: allPortions
    });
    
    setCustomPortions([]);
    setShowUpdateDialog(false);
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="recipe-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-content">
            <h2>
              {currentLoadedRecipe 
                ? currentLoadedRecipe.name 
                : (isGerman ? 'Aktuelles Rezept' : 'Current Recipe')
              }
            </h2>
          </div>
          <button 
            className="modal-close-button"
            onClick={onClose}
            aria-label={isGerman ? 'Schließen' : 'Close'}
          >
            ×
          </button>
        </div>
        
        {ingredients.length === 0 ? (
          <div className="empty-recipe">
            <p>{isGerman ? 'Keine Zutaten im Rezept' : 'No ingredients in recipe'}</p>
          </div>
        ) : (
          <div className="recipe-content">
            <div className="ingredients-list">
              <h3>{isGerman ? 'Zutaten' : 'Ingredients'}</h3>
              {ingredients.map((ingredient) => (
                <div key={ingredient.id} className="ingredient-item">
                  <div className="ingredient-info">
                    <div className="ingredient-compact">
                      <div className="ingredient-left">
                        <span className="ingredient-name">{ingredient.foodName}</span>
                        <span className="ingredient-portion">({ingredient.portionName})</span>
                      </div>
                      <div className="ingredient-right">
                        <span className="ingredient-carbs">
                          {formatCarbs(ingredient.carbs, preferences.carbUnit, preferences.language)}
                        </span>
                        {preferences.showFPU && (
                          <span className="ingredient-fpu">
                            {calculateFPU(ingredient.fat, ingredient.protein).totalFPU.toFixed(1)} {isGerman ? 'FPE' : 'FPU'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button 
                    className="remove-ingredient-button"
                    onClick={() => handleRemoveIngredient(ingredient.id)}
                    title={isGerman ? 'Zutat entfernen' : 'Remove ingredient'}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            
            <div className="recipe-totals">
              <h3>{isGerman ? 'Gesamt' : 'Total'}</h3>
              <div className="totals-display">
                <div className="total-item">
                  <span className="total-label">{isGerman ? 'Gewicht:' : 'Weight:'}</span>
                  <span className="total-value">{totals.grams.toFixed(0)}g</span>
                  <span className="total-label carbs-label">{isGerman ? 'KH:' : 'Carbs:'}</span>
                  <span className="total-value carbs-value">
                    {formatCarbs(totals.carbs, preferences.carbUnit, preferences.language)}
                  </span>
                </div>
                {preferences.showFPU && totalFPU.totalFPU > 0 && (
                  <div className="total-item">
                    <span className="total-label">{isGerman ? 'FPE:' : 'FPU:'}</span>
                    <span className="total-value fpu-value">
                      {totalFPU.totalFPU.toFixed(1)}
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="recipe-portions">
              <h3>{isGerman ? 'Portionen' : 'Servings'}</h3>
              
              <div className="add-portion">
                <div className="portion-inputs">
                  <input
                    type="text"
                    className="portion-name-input"
                    placeholder={isGerman ? 'Portionsname (z.B. "1 Teller")' : 'Serving name (e.g. "1 plate")'}
                    value={portionName}
                    onChange={(e) => setPortionName(e.target.value)}
                  />
                  <input
                    type="number"
                    className="portion-grams-input"
                    placeholder={isGerman ? 'Gramm' : 'Grams'}
                    value={portionGrams}
                    onChange={(e) => setPortionGrams(e.target.value)}
                    min="0"
                    step="0.1"
                  />
                  <button 
                    className="add-portion-button"
                    onClick={handleAddPortion}
                    disabled={!portionName.trim() || !portionGrams.trim()}
                  >
                    {isGerman ? 'Hinzufügen' : 'Add'}
                  </button>
                </div>
              </div>
              
              {customPortions.length > 0 && (
                <div className="portions-list">
                  {customPortions.map((portion, index) => {
                    const nutrients = calculatePortionNutrients(portion.grams);
                    const fpuResult = calculateFPU(nutrients.fat, nutrients.protein);
                    
                    return (
                      <div key={index} className="portion-item">
                        <div className="portion-info">
                          <div className="portion-name">{portion.name}</div>
                          <div className="portion-details">
                            <span className="portion-weight">{portion.grams}g</span>
                            <span className="portion-carbs">
                              {formatCarbs(nutrients.carbs, preferences.carbUnit, preferences.language)}
                            </span>
                            {preferences.showFPU && fpuResult.totalFPU > 0 && (
                              <span className="portion-fpu">
                                {fpuResult.totalFPU.toFixed(1)} {isGerman ? 'FPE' : 'FPU'}
                              </span>
                            )}
                          </div>
                        </div>
                        <button 
                          className="remove-portion-button"
                          onClick={() => handleRemovePortion(index)}
                          title={isGerman ? 'Portion entfernen' : 'Remove serving'}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="recipe-actions">
              <button 
                className="clear-recipe-button"
                onClick={handleClearRecipe}
              >
                {isGerman ? 'Verwerfen' : 'Discard'}
              </button>
              
              {currentLoadedRecipe ? (
                <>
                  <button 
                    className="update-recipe-button"
                    onClick={handleUpdateRecipe}
                  >
                    {isGerman ? 'Rezept aktualisieren' : 'Update recipe'}
                  </button>
                  <button 
                    className="save-recipe-button"
                    onClick={handleSaveNewRecipe}
                  >
                    {isGerman ? 'Neues Rezept speichern' : 'Save new recipe'}
                  </button>
                </>
              ) : (
                <button 
                  className="save-recipe-button"
                  onClick={handleSaveNewRecipe}
                >
                  {isGerman ? 'Rezept speichern' : 'Save recipe'}
                </button>
              )}
            </div>
          </div>
        )}
        
        {/* Save Recipe Dialog */}
        {showSaveDialog && (
          <div className="save-dialog-overlay">
            <div className="save-dialog">
              <h3>{isGerman ? 'Rezept speichern' : 'Save Recipe'}</h3>
              <input
                type="text"
                className="recipe-name-input"
                placeholder={isGerman ? 'Rezeptname eingeben...' : 'Enter recipe name...'}
                value={recipeName}
                onChange={(e) => setRecipeName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveConfirm();
                  }
                }}
                autoFocus
              />
              <div className="save-dialog-actions">
                <button 
                  className="cancel-save-button"
                  onClick={() => {
                    setShowSaveDialog(false);
                    setRecipeName('');
                  }}
                >
                  {isGerman ? 'Abbrechen' : 'Cancel'}
                </button>
                <button 
                  className="confirm-save-button"
                  onClick={handleSaveConfirm}
                  disabled={!recipeName.trim()}
                >
                  {isGerman ? 'Speichern' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Update Recipe Dialog */}
        {showUpdateDialog && (
          <div className="save-dialog-overlay">
            <div className="save-dialog">
              <h3>{isGerman ? 'Rezept aktualisieren' : 'Update Recipe'}</h3>
              <p>
                {isGerman 
                  ? `Möchtest Du die Änderungen an "${currentLoadedRecipe?.name}" speichern?`
                  : `Do you want to save the changes to "${currentLoadedRecipe?.name}"?`
                }
              </p>
              <div className="save-dialog-actions">
                <button 
                  className="cancel-save-button"
                  onClick={() => setShowUpdateDialog(false)}
                >
                  {isGerman ? 'Abbrechen' : 'Cancel'}
                </button>
                <button 
                  className="confirm-save-button"
                  onClick={handleUpdateConfirm}
                >
                  {isGerman ? 'Aktualisieren' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};